import React, { ReactElement, useEffect, useMemo, useRef } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import {
  Modal,
  Button,
  Form,
  Alert,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import './LiveSettings.scss';
import { LiveProps } from '../../LiveContainer';
import {
  useGetJoinableEventListQuery,
  useGetLiveCategoryListQuery,
  useGetPrivateEventsMutation,
  useGetStreamKeyMutation,
  useGetUserInfoQuery,
  usePutMirrorOnMutation,
  usePutMuteOffMutation,
  useRequestStartLiveMutation,
} from '../../../../services/api/apiProvider';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import BarLoader from 'react-spinners/BarLoader';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { useTranslation } from 'react-i18next';
import { ResponsePrivateEvent } from '../../../../services/api/models/response/responseEvent';
import { RequestStartLive } from '../../../../services/api/models/request/requestStartLive';
import { reproio } from '../../../../common/utils/reproio';
import { ErrorResponse } from '../../../../common/constants/globalConstants';
import { APP_BASE_URL } from '../../../../loadEnv';
import { useDispatch, useSelector } from 'react-redux';
import { FiHelpCircle as IconHelp } from 'react-icons/fi';
import { AppState } from '../../../../store/store';
import { convertDataToUID } from '../../../../common/utils/utils';
import { Debug } from '../../../../common/utils/debugLogs';

export interface MediaDevice {
  id: string;
  name: string;
  kind: MediaDeviceKind;
}

export const STREAM_KEY_REGION = 'ap';

export const enableLiveMatch = false;

export const LiveSettings = (props: LiveProps): ReactElement => {
  const history = useHistory();
  const { data: liveCategory } = useGetLiveCategoryListQuery();
  const { data: listEvents } = useGetJoinableEventListQuery();
  // user data from query api
  const { data: user, isFetching } = useGetUserInfoQuery();
  const [getPrivateEvents] = useGetPrivateEventsMutation();
  const [getStreamKey] = useGetStreamKeyMutation();

  const [startLive, { isLoading, isError }] = useRequestStartLiveMutation();
  const [turnOffMute] = usePutMuteOffMutation();
  const [turnOnMirror] = usePutMirrorOnMutation();

  const dispatch = useDispatch();
  const [eventId, setEventId] = useState<any>(localStorage.getItem('eventId'));
  const liveIdToSend = useRef<undefined | string>();

  const [name, setName] = useState<string | undefined | null>(
    localStorage.getItem('titleLive'),
  );
  const isUseStreamKey = useSelector(
    (state: AppState) => state.live.isUseStreamKey,
  );
  const [streamKey, setStreamKey] = useState('');
  const [pushURL] = useState(
    `rtmp://rtls-ingress-prod-${STREAM_KEY_REGION}.agoramdn.com/live`,
  );
  const [isShowNotifyOBSSetting, setIsShowNotifyOBSSetting] = useState(false);

  const privateEvents = useRef<Array<ResponsePrivateEvent>>([]);

  const { t } = useTranslation();
  const formLabelStyle = { fontSize: '0.8rem' };

  let liveCategoryId = '';

  useMemo(() => {
    if (listEvents?.findIndex((event) => event.id === eventId) === -1) {
      setEventId(listEvents[0].id);
      localStorage.setItem('eventId', listEvents[0].id);
    }
  }, [listEvents]);

  const onCloseModal = () => {
    // when close modal, go home if not in live
    if (!props.liveState.isInLive) {
      history.push('/');
    }
  };

  const handleClose = () => {
    props.toggleSettingsModal(false);
  };

  // update when user select live category
  const onLiveCategoryChange = (event: any) => {
    liveCategoryId = event.target.value;
  };

  // update live name
  const onLiveNameChange = (event: any) => {
    setName(event.target.value);
    localStorage.setItem('titleLive', event.target.value);
  };

  // update event
  const onSelectEvent = (event: any) => {
    let value = event.target.value;
    if (value == undefined || value === '--') value = null;
    localStorage.setItem('eventId', value);
    setEventId(value);
  };

  // update video input
  const onSelectVideoInput = (event: any) => {
    const value =
      event.target.value !== 'None' ? event.target.value : undefined;
    props.changeVideoInput(value);
  };

  // update audio input
  const onSelectAudioInput = (event: any) => {
    const value =
      event.target.value !== 'None' ? event.target.value : undefined;
    props.changeAudioInput(value);
  };

  // update audio output
  const onSelectAudioOutput = (event: any) => {
    props.changeAudioOutput(event.target.value);
  };

  // update enable gift
  const onSelectEnableGift = (event: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(props.updateEnableGift(!!Number(event.target.value)));
  };

  const onTextBoxKeyDown = (event: any) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  useEffect(() => {
    const liveId = self.crypto.randomUUID();
    liveIdToSend.current = liveId;

    if (props.liveState.isLive && user) {
      getStreamKey({
        channel: liveId,
        expire: 86400,
        uid: String(convertDataToUID(user?.id)),
      })
        .unwrap()
        .then((res) => {
          setStreamKey(res.streamKey);
        })
        .catch((e) => {
          Debug.error('Get streamKey error:', e);
        });
    }
  }, [props.liveState.isLive, !!user]);

  // when user click start live
  const onClickStartLive = (): void => {
    if (!name || name.length <= 0) {
      toast.error('配信タイトルを設定してください。');
      return;
    }

    if (liveCategoryId === '') {
      liveCategoryId = (
        document.querySelector(
          '#live-setting__live-category',
        ) as HTMLInputElement
      ).value;
    }

    if (eventId === null) {
      let evId: string | null = (
        document.querySelector('#live-setting__event') as HTMLInputElement
      ).value;
      if (evId == undefined) evId = null;
      setEventId(evId);
    }

    let privateEventId = null;
    privateEvents?.current.forEach((event) => {
      if (Date.parse(event.endDateTime) > Date.now()) {
        privateEventId = event.id;
      }
    });

    let pairType: string | undefined = 'none';
    let kind: string | undefined = 'normal';
    if (!props.liveState.isLive) {
      props.changeVideoInput(undefined);
      pairType = 'none';
      kind = 'radio';
    }

    const requestStartLive: RequestStartLive = {
      liveHashtagIds: null,
      liveId: liveIdToSend.current,
      privateEventId: privateEventId,
      name,
      eventId,
      liveCategoryId,
      pairType: pairType,
      kind: kind,
      match: enableLiveMatch,
      canReceivedGift: props.liveState.isEnableGift,
    };

    if (eventId != undefined) {
      reproio('track', 'complete subscribe event', {
        event_name: listEvents?.find((event) => event.id === eventId)?.name,
      });
    }

    // dispatch start live action
    startLive(requestStartLive)
      .unwrap()
      .then((responseLiveInfo) => {
        if (responseLiveInfo.isFirstStreaming) {
          reproio('track', 'complete first broadcast');
        }
        props.toggleSettingsModal(false);
        setIsShowNotifyOBSSetting(false);
        props.startLive(responseLiveInfo);
        turnOffMute(responseLiveInfo.id); // enable audio when start live by default
        turnOnMirror(responseLiveInfo.id); // enable mirror when start live by default
      })
      .catch((error: ErrorResponse) => {
        if (error.data.errorCode === 503) {
          history.push('/maintenance');
        } else {
          toast.error(error.data.errorMessage);
        }
      });
  };

  const showNotifyOBSSetting = () => {
    setIsShowNotifyOBSSetting(true);
  };

  useEffect((): void => {
    // redirect to top page if user is unverified
    if (user !== undefined && user.liverLeague !== 1) {
      alert(t('RequireVerified'));
      window.location.href = `${window.location.origin}/${APP_BASE_URL}`;
      return;
    }

    // load private event after load user
    if (user !== undefined) {
      getPrivateEvents(user.id)
        .unwrap()
        .then((result: ResponsePrivateEvent[]) => {
          privateEvents.current = result;
        });
    }

    // check video/audio permission and get IO devices
    AgoraRTC.getDevices()
      .then((devices) => {
        const listInputDevice = devices.map((device) => {
          const temp: MediaDevice = {
            id: device.deviceId,
            name: device.label,
            kind: device.kind,
          };
          return temp;
        });
        props.updateListMediaDevices(listInputDevice);
      })
      .catch(() => {
        toast.error('デバイスへの接続に失敗しました。');
      });
  }, [isFetching]);

  const handleChangeUseStreamKey = (e: any) => {
    if (e.target.id === 'use-OBS') {
      dispatch(props.updateIsUseStreamKey(true));
    } else {
      dispatch(props.updateIsUseStreamKey(false));
    }
  };

  const handleCopyText = (id: string) => () => {
    const copyText = document.querySelector(
      `input#${id}`,
    ) as HTMLInputElement | null;

    if (!copyText) return;
    // Select the text field
    copyText.select();
    copyText.setSelectionRange(0, 99999); // For mobile devices

    // Copy the text inside the text field
    navigator.clipboard.writeText(copyText.value);
  };

  if (isLoading || isFetching) {
    return (
      <div className="d-flex justify-content-center">
        <BarLoader color={'#0275d8'} width="100%" cssOverride={{}}></BarLoader>
      </div>
    );
  }

  if (isError) {
    return <Alert variant="danger">Error</Alert>;
  }

  if (user !== undefined && user.liverLeague !== 1) {
    return <></>;
  }

  return (
    <>
      <Modal
        show={props.liveState.isShowSettingModal}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
        centered
        scrollable={true}
      >
        <Modal.Header closeButton onHide={onCloseModal}></Modal.Header>
        <Modal.Body>
          <Form>
            {/* Title */}
            <Form.Label style={formLabelStyle}>タイトル</Form.Label>
            <Form.Control
              id="live-setting__live-name"
              placeholder="タイトル"
              value={name || ''}
              disabled={props.liveState.isInLive}
              onChange={(event) => onLiveNameChange(event)}
              onKeyDown={(event) => onTextBoxKeyDown(event)}
            />

            {/* Category */}
            <p></p>
            <Form.Label style={formLabelStyle}>カテゴリー</Form.Label>
            <Form.Select
              id="live-setting__live-category"
              value={props.liveState.liveData?.liveCategory?.id}
              disabled={props.liveState.isInLive}
              onChange={(event) => onLiveCategoryChange(event)}
            >
              {liveCategory?.map((category, index) => (
                <option key={index} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Form.Select>

            {/* Events */}
            <p></p>
            <Form.Label style={formLabelStyle}>イベント</Form.Label>
            <Form.Select
              id="live-setting__event"
              value={eventId}
              disabled={props.liveState.isInLive}
              onChange={(event) => onSelectEvent(event)}
            >
              {listEvents?.map((event, index) => {
                if (index === 0 && eventId === null && event.id !== undefined) {
                  setEventId(event.id);
                  localStorage.setItem('eventId', event.id);
                }
                return (
                  <option key={index} value={event.id}>
                    {event.name}
                  </option>
                );
              })}
            </Form.Select>

            {/* Choose enable gift*/}
            {!props.liveState.isInLive && (
              <>
                <p></p>
                <Form.Label style={formLabelStyle}>
                  ギフトの受け取り設定
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Tooltip className="live-setting__help-button__tooltip">
                        配信中のギフトの受け取りを設定することができます。「受け取らない」に設定すると、リスナーがギフトを贈ることができなくなります。また、マッチ配信に参加することができなくなります。
                      </Tooltip>
                    }
                  >
                    <button
                      className="live-setting__help-button"
                      onClick={(e) => {
                        e.preventDefault();
                      }}
                    >
                      <IconHelp />
                    </button>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Select
                  id="live-setting__enable-gift"
                  onChange={(e) => onSelectEnableGift(e)}
                  value={props.liveState.isEnableGift ? 1 : 0}
                >
                  <option value={1}>受け取る</option>
                  <option value={0}>受け取らない</option>
                </Form.Select>
              </>
            )}

            {/* Choose use Stream key Obs or not */}
            {props.liveState.isLive && (
              <div style={{ marginTop: '17px' }}>
                <Form.Check
                  inline
                  label="No using OBS"
                  name="useObs"
                  type={'radio'}
                  id={`no-use-OBS`}
                  defaultChecked={!isUseStreamKey}
                  onChange={handleChangeUseStreamKey}
                  disabled={props.liveState.isInLive}
                />
                <Form.Check
                  inline
                  label="Using OBS"
                  name="useObs"
                  type={'radio'}
                  id={`use-OBS`}
                  defaultChecked={isUseStreamKey}
                  onChange={handleChangeUseStreamKey}
                  disabled={props.liveState.isInLive}
                />
              </div>
            )}

            {isUseStreamKey ? (
              <>
                {/* Push URL */}
                <p></p>
                <Form.Label style={formLabelStyle}>Push URL</Form.Label>
                <div className="live-setting__live-streamkey-wrapper">
                  <Form.Control
                    id="live-setting__live-pushURL"
                    value={pushURL}
                    readOnly
                  />
                  <Button
                    size="sm"
                    variant="dark"
                    onClick={handleCopyText('live-setting__live-pushURL')}
                  >
                    Copy
                  </Button>
                </div>
                {/* Stream key */}
                <p></p>
                <Form.Label style={formLabelStyle}>Stream key</Form.Label>
                <div className="live-setting__live-streamkey-wrapper">
                  <Form.Control
                    id="live-setting__live-streamKey"
                    value={streamKey}
                    readOnly
                  />
                  <Button
                    size="sm"
                    variant="dark"
                    onClick={handleCopyText('live-setting__live-streamKey')}
                  >
                    Copy
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Video input device */}
                <p></p>
                <Form.Label style={formLabelStyle}>カメラ選択</Form.Label>
                <Form.Select
                  id="live-setting__video-input"
                  onChange={(event) => onSelectVideoInput(event)}
                  value={props.liveState.inputVideoDevice}
                >
                  <>
                    <option>None</option>
                    {props.liveState.mediaDevices
                      .filter((device) => device.kind === 'videoinput')
                      .map((device, index) => (
                        <option key={index} value={device.id}>
                          {device.name}
                        </option>
                      ))}
                  </>
                </Form.Select>

                {/* Audio input device */}
                <p></p>
                <Form.Label style={formLabelStyle}>オーディオの選択</Form.Label>
                <Form.Select
                  id="live-setting__audio-input"
                  onChange={(event) => onSelectAudioInput(event)}
                  value={props.liveState.inputAudioDevice}
                >
                  <>
                    <option>None</option>
                    {props.liveState.mediaDevices
                      .filter((device) => device.kind === 'audioinput')
                      .map((device, index) => (
                        <option key={index} value={device.id}>
                          {device.name}
                        </option>
                      ))}
                  </>
                </Form.Select>

                {/* Audio output device */}
                <p></p>
                <Form.Label style={formLabelStyle}>スピーカー</Form.Label>
                <Form.Select
                  id="live-setting__audio-output"
                  onChange={(event) => onSelectAudioOutput(event)}
                  value={props.liveState.outputAudioDevice}
                >
                  {props.liveState.mediaDevices
                    .filter((device) => device.kind === 'audiooutput')
                    .map((device, index) => (
                      <option key={index} value={device.id}>
                        {device.name}
                      </option>
                    ))}
                </Form.Select>
              </>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer style={{ justifyContent: 'center' }}>
          {!props.liveState.isInLive && (
            <Button
              variant="primary"
              onClick={isUseStreamKey ? showNotifyOBSSetting : onClickStartLive}
            >
              配信開始
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        limit={1}
        pauseOnHover={false}
      />
      {/* Modal to notify if OBS is set up */}
      <Modal
        show={isShowNotifyOBSSetting}
        backdrop="static"
        keyboard={false}
        centered
        className="live-setting__OBS-modal"
      >
        <Modal.Header>
          <p className="live-setting__OBS-modal__header">
            OBSの設定確認をお願いします
          </p>
        </Modal.Header>
        <Modal.Body>
          <p className="live-setting__OBS-modal__content">
            StreamKeyの設定はしましたか？
            <br />
            OBSは配信開始になっていますか？
          </p>
          <p className="live-setting__OBS-modal__note">
            ※OBSでの設定が不十分だと正しく配信できない場合があります
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={onClickStartLive}>
            配信開始
          </Button>
          <Button
            variant="secondary"
            onClick={() => setIsShowNotifyOBSSetting(false)}
          >
            キャンセル
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
